/* istanbul ignore file */
import { fetchTopBannerContent } from '@noths/polaris-client-banners';
import { FeatureFlags } from '@noths/polaris-client-feature-flags';
import { fetchNavigationContent } from '@noths/polaris-client-site-chrome-ui';
import { CachingMethod, UseRedisCacheForContentStack } from '@noths/polaris-dev-ts-types';
import { Config, Environment, fetchContentByQuery } from '@noths/polaris-server-content-fetching';
import * as runtimeProcess from 'process';

import { ContentstackSections, ContentstackSeo } from 'src/types/Contentstack';
import { PageConfiguration, PageMetadata } from 'src/types/PageProps';
import { logger } from 'src/utils/serverLogger';
import { DEVELOPMENT, QA } from '../constants/environment';

export const getContentstackEnvironment = (isPreview?: boolean) => {
  if (isPreview) {
    return Environment.Preview;
  }
  const isQa = runtimeProcess.env.ENV_TYPE === QA || process.env.NODE_ENV === DEVELOPMENT;

  return isQa ? Environment.Qa : Environment.Production;
};

interface RibbonsPageContentEntry {
  sections: ContentstackSections[];
  seo: ContentstackSeo;
  title: string;
  category_id?: string;
}

interface GlobalContentOptions {
  featureFlags: FeatureFlags;
  urlPath?: string;
  isPreview?: boolean;
}

export const getExperimentCachingMethod = (featureFlags: FeatureFlags): CachingMethod =>
  featureFlags[UseRedisCacheForContentStack.NAME] ===
  UseRedisCacheForContentStack.VARIANT_REDIS_CACHE
    ? 'redis'
    : 'memory';

export const fetchGlobalContent = async ({
  featureFlags,
  urlPath,
  isPreview = false,
}: GlobalContentOptions) => {
  const env = getContentstackEnvironment(isPreview);
  const cachingMethod = getExperimentCachingMethod(featureFlags);
  const config = { env, logger, cachingMethod };
  const navContentVariantId = featureFlags.nav_content_experiment;

  return Promise.all([
    fetchNavigationContent(config, navContentVariantId).catch(() => null),
    fetchTopBannerContent(config, urlPath),
  ]);
};

export const fetchPageContentByQuery = async (
  { contentType }: PageConfiguration,
  { featureFlags, isPreview, pathname }: PageMetadata,
) => {
  const config: Config = {
    env: getContentstackEnvironment(isPreview),
    logger,
    stackName: 'Ribbons',
    cachingMethod: getExperimentCachingMethod(featureFlags),
  };

  const data = await fetchContentByQuery(config, contentType, { url: pathname });

  return data.content as RibbonsPageContentEntry;
};
